/** @jsx jsx */
import { jsx } from '@emotion/core'
import React, { useState, useEffect } from 'react'
import { intCol, numCol } from '@integec/grid-tools/lib/cols'
import { toSelectionColProps } from '@integec/grid-tools/lib/utils'
import {
  modifierCol,
  modifiedDateCol,
  utilityHeader,
  unitHeader,
} from 'core/HeaderColTypeProps'
import { getApiTargetInfo, UtilityUnitSelection } from 'core'
import { connect } from 'react-redux'
import R from 'ramda'

import {
  postNERCCapacityInfo,
  deleteNERCCapacityInfo,
  getNERCCapacityInfoOnUnit,
} from 'api/pgads.js'
import {
  getUtilityNameMap,
  getUnitNameMap,
  getUtilityUnitsMap,
  getPermitActions,
  getEnvConfig,
} from 'core/selectors'
import CommonScreen from './CommonScreen'
import { Select, Button, InputNumber } from 'antd'

import { hasAccesPermits } from 'core/utils'
import * as ScreenPermissions from 'core/EventActions'

const { Option } = Select

// const retrieveTask = apiTargetInfo => () => getNERCCapacityInfo(apiTargetInfo)

const retrieveTask = apiTargetInfo => units =>
  getNERCCapacityInfoOnUnit(apiTargetInfo)(units)

const modifyTask = apiTargetInfo => data =>
  postNERCCapacityInfo(apiTargetInfo)(data)

const deleteTask = apiTargetInfo => data =>
  deleteNERCCapacityInfo(apiTargetInfo)(data)

const monthOption = R.range(1, 13).map(i => ({
  key: `${i}`,
  value: `${i}`,
  text: `${i}`,
}))

const headers = [
  utilityHeader,
  unitHeader,
  intCol({
    ident: 'month',
    display: 'Month',
    isKey: true,
  }),
  numCol({
    ident: 'netDepCap',
    display: 'Net Dependable Capacity',
    isRequired: true,
  }),
  numCol({
    ident: 'grossDepCap',
    display: 'Gross Dependable Capacity',
  }),
  modifierCol,
  modifiedDateCol,
]

const title = 'NERC Capacity'

const NERCCapacity = props => {
  const [insertEvent, setInsertEvent] = useState(false)
  // const [] = useState({ netDepCap: 0, grossDepCap: 0 })
  const [withoutModify, setWithoutModify] = useState(true)

  const {
    utilityNameMap,
    unitNameMap,
    utilityUnitMap,
    envConfig,
    actionPermits,
  } = props

  const generateDataGetter = ({ rowData, header }) => {
    if (header.ident === 'unit') header.choices = []
    if (rowData.utility !== '' && rowData.utility !== undefined) {
      if (header.ident === 'unit') {
        const unitList = utilityUnitMap && utilityUnitMap[rowData.utility]
        header.choices = unitList
          ? unitList.map(value => ({
              text: unitNameMap[value] || value,
              value,
            }))
          : []

        return rowData.unit
      }
    }
  }

  useEffect(() => {
    const isAdmin = hasAccesPermits({
      authGodMode: envConfig.authGodMode,
      actionPermits,
      eventActions: [ScreenPermissions.NERC_CAPACITY_USER_ADMIN],
      matchAny: true,
    })
    setWithoutModify(!isAdmin)
  }, [actionPermits, envConfig.authGodMode])

  const withImport = hasAccesPermits({
    authGodMode: envConfig.authGodMode,
    actionPermits,
    eventActions: [ScreenPermissions.NERCCAP_IMPORT],
    matchAny: true,
  })

  const headerWithFormatter = headers.map(header =>
    header.ident === 'utility'
      ? {
          ...header,
          ...toSelectionColProps(utilityNameMap),
          dataFormatter: ({ value }) => utilityNameMap[value] || value,
          sortIndexGetter: ({ rowData }) =>
            utilityNameMap[rowData.utility] || rowData.utility,
        }
      : header.ident === 'unit'
      ? {
          ...header,
          choices: [],
          dataGetter: generateDataGetter,
          dataFormatter: ({ value }) => unitNameMap[value] || value,
          sortIndexGetter: ({ rowData }) =>
            unitNameMap[rowData.unit] || rowData.unit,
        }
      : header.ident === 'month'
      ? {
          ...header,
          choices: monthOption,
        }
      : header
  )

  const exportHeader = headers.map(header =>
    header.ident === 'unit'
      ? {
          ...header,
          dataGetter: ({ rowData }) => rowData?.unit?.substr(3),
        }
      : header
  )

  const insertComp = ({ insertData, setInsertData, onAdd, onCancel }) => (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: '100px 450px 180px 100px 300px',
        gridGap: '10px',
        marginBottom: '20px',
      }}
    >
      <div>Utility / Unit :</div>
      <UtilityUnitSelection
        className="utilityUnit"
        utility={insertData.utility}
        unit={insertData.unit}
        onUnitChange={value => {
          // setUtilityUnit(value)
          setInsertData({
            ...insertData,
            utility: value[0],
            unit: value[1],
          })
        }}
      />
      <div>Net Dependable Capacity :</div>
      <InputNumber
        className="netDependableCapacity"
        defaultValue={0}
        onChange={value => setInsertData({ ...insertData, netDepCap: value })}
      />
      <div />
      <div>Month :</div>
      <Select
        className="month"
        value={insertData.month}
        style={{ maxWidth: '150px' }}
        onChange={value => setInsertData({ ...insertData, month: value })}
      >
        {monthOption.map(m => (
          <Option key={m} value={m.key}>
            {m.value}
          </Option>
        ))}
      </Select>

      <div>Gross Dependable Capacity : </div>
      <InputNumber
        className="grossDependableCapacity"
        defaultValue={0}
        onChange={value => setInsertData({ ...insertData, grossDepCap: value })}
      />
      <div style={{ gap: '10px', display: 'flex', justifyContent: 'flex-end' }}>
        <Button type="primary" onClick={() => onAdd(true)}>
          Add
        </Button>
        <Button type="primary" onClick={() => onAdd(false)}>
          Add & Hide
        </Button>
        <Button type="danger" onClick={onCancel}>
          Cancel
        </Button>
      </div>
    </div>
  )

  return (
    <CommonScreen
      key="nerccap"
      title={title}
      isAdminScreen={false}
      header={headerWithFormatter}
      exportHeader={exportHeader}
      saveTask={modifyTask}
      retrieveTask={retrieveTask}
      withCriteria={(state, props, selectedUnits) => selectedUnits}
      withUnitSelection
      deleteTask={deleteTask}
      autoRetrieve={false}
      altBy={d => d.unit}
      sortOptions={[utilityHeader, unitHeader]}
      allowInsert={!withoutModify}
      insertComp={insertEvent && insertComp}
      insertCompHeight={150}
      onInsertEvent={b => setInsertEvent(b)}
      headerRowHeight={60}
      defaultInsertData={{ netDepCap: 0, grossDepCap: 0, month: 1 + '' }}
      withoutModify={withoutModify}
      withImport={withImport}
      importUrl="screen-csv-import-file"
      importData={{ importType: 'NERC_CAPACITY' }}
    />
  )
}

const mapStateToProps = state => ({
  apiTargetInfo: getApiTargetInfo(state),
  utilityNameMap: getUtilityNameMap(state),
  unitNameMap: getUnitNameMap(state),
  utilityUnitMap: getUtilityUnitsMap(state),
  actionPermits: getPermitActions(state),
  envConfig: getEnvConfig(state),
})

export default connect(mapStateToProps)(NERCCapacity)
